import Box from '@/common/components/Box'
import { cn } from '@/common/utils/cn'
import DirectiveEntryDiagnosticEntryCompleted from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/DiagnosticEntryCompleted'
import DirectiveEntryDiagnosticEntriesAdvance from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Entries/DiagnosticEntry/Advance'
import DirectiveEntryDiagnosticEntriesApprove from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Entries/DiagnosticEntry/Approve'
import DirectiveEntryDiagnosticEntriesDisapprove from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Entries/DiagnosticEntry/Disapprove'
import DirectiveEntryDiagnosticEntriesRegress from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Entries/DiagnosticEntry/Regress'
import {
  MaintenanceForwardingSectionStyle,
  maintenanceForwardingSectionStyles,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingSectionStyles.constant'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { MaintenanceForwardingDirectiveEntryStatus } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import { useMemo } from 'react'

const MaintenanceWorkflowForwardingDirectiveEntrySectionDiagnosticEntry = () => {
  const { forwarding, forwardingUpdatedAt, noActions } =
    useForwardingTabContext<MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry>()

  const isDiagnosticEntryPending = useMemo(() => {
    return (
      forwarding?.directive_entry?.status &&
      [
        MaintenanceForwardingDirectiveEntryStatus.AwaitingManualStart,
        MaintenanceForwardingDirectiveEntryStatus.InProgress,
      ].includes(forwarding?.directive_entry?.status)
    )
  }, [forwardingUpdatedAt])

  const isDiagnosticEntryCompleted = useMemo(() => {
    return (
      (forwarding?.directive_entry?.status &&
        ![
          MaintenanceForwardingDirectiveEntryStatus.Pending,
          MaintenanceForwardingDirectiveEntryStatus.AwaitingManualStart,
          MaintenanceForwardingDirectiveEntryStatus.InProgress,
        ].includes(forwarding?.directive_entry?.status)) ||
      (forwarding?.directive_entry?.status === MaintenanceForwardingDirectiveEntryStatus.InProgress &&
        forwarding?.directive_entry?.last_review?.state === 'disapproved')
    )
  }, [forwardingUpdatedAt])

  const isDiagnosticEntryAwaitingClientReview = useMemo(() => {
    return (
      forwarding?.directive_entry?.status &&
      [MaintenanceForwardingDirectiveEntryStatus.AwaitingClientReview].includes(forwarding?.directive_entry?.status)
    )
  }, [forwardingUpdatedAt])

  const isDiagnosticEntryDisapproved = useMemo(() => {
    return forwarding?.directive_entry?.last_review?.state === 'disapproved'
  }, [forwardingUpdatedAt])

  const sectionStyle = useMemo(() => {
    if (isDiagnosticEntryAwaitingClientReview) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Pending]
    }

    if (isDiagnosticEntryPending) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Waiting]
    }

    if (isDiagnosticEntryCompleted) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Completed]
    }

    return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Default]
  }, [isDiagnosticEntryCompleted, isDiagnosticEntryAwaitingClientReview, isDiagnosticEntryPending])

  return (
    <Box className={cn('p-4 flex flex-col gap-4', sectionStyle)}>
      <div className="flex items-center justify-between flex-wrap">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">Diagnóstico e orçamento</p>

          {isDiagnosticEntryPending && (
            <p className="text-warning text-xs">
              Aguardando estabelecimento realizar análise do diagnóstico e orçamento
            </p>
          )}

          {forwarding?.directive_entry?.status === MaintenanceForwardingDirectiveEntryStatus.Completed && (
            <p className="text-valid text-xs">Diagnóstico e orçamento concluído</p>
          )}
        </div>

        {isDiagnosticEntryAwaitingClientReview && !noActions && (
          <div className="flex items-center gap-3">
            {isDiagnosticEntryDisapproved ? (
              <>
                <DirectiveEntryDiagnosticEntriesApprove />

                <DirectiveEntryDiagnosticEntriesDisapprove
                  buttonText="Alterar revisão"
                  defaultDescription={forwarding.directive_entry.last_review?.description}
                />

                <DirectiveEntryDiagnosticEntriesRegress />
              </>
            ) : (
              <>
                <DirectiveEntryDiagnosticEntriesDisapprove />

                <DirectiveEntryDiagnosticEntriesAdvance />
              </>
            )}
          </div>
        )}
      </div>

      {forwarding?.directive_entry && isDiagnosticEntryCompleted && (
        <DirectiveEntryDiagnosticEntryCompleted
          lastReview={forwarding.directive_entry.last_review}
          processStartedAt={forwarding.directive_entry.process_started_at}
          completedAt={forwarding.directive_entry.completed_at}
          technicalResponse={forwarding.directive_entry.technical_response}
          deadLineToCompleteInDays={forwarding.directive_entry.deadline_to_complete_in_days}
          totalValue={forwarding.directive_entry.total_value}
        />
      )}
    </Box>
  )
}

export default MaintenanceWorkflowForwardingDirectiveEntrySectionDiagnosticEntry
